import { useLocalization } from "@fluent/react";
import { Button, Row } from "@narmi/design_system";
import { useEffect, useState } from "react";
import { BackButton } from "@lending/components/BackButton/BackButton";
import { ErrorScreen } from "@lending/components/ErrorScreen/ErrorScreen";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { PersonalLoanSelectedProductTag } from "@lending/components/SelectedProductTag/PersonalLoanSelectedProductTag";
import { useListApplicationApplicantDetailsQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/useListApplicationApplicantDetailsQuery";
import { useGetPersonalLoanApplicationQuery } from "@lending/hooks/queries/PersonalLoanApplication/useGetPersonalLoanApplicationQuery";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useCurrentApplicationUuid } from "@lending/hooks/useCurrentApplicationUuid";
import { useToastsContext } from "@lending/hooks/useToasts";
import { useFlowNavigation } from "@lending/navigation";
import { InfoAlert } from "@lending/pages/Common/components/InfoAlert";
import { ApplicationApplicantDetailsRead } from "@lending/types";
import { isMissingContactDetails } from "@lending/utils/applicantHelpers";
import { ApplicantConfirmCard } from "./components/ApplicantConfirmCard/ApplicantConfirmCard";
import { EditInfoDrawer } from "./components/EditInfoDrawer/EditInfoDrawer";
import styles from "./ConfirmInfo.module.css";
import { useStore } from "../../../contexts/StoreContext";

function ConfirmInfo() {
  const { l10n } = useLocalization();
  const { institution } = useGetInstitutionQuery();
  const { goNext, goToPath } = useFlowNavigation();
  const { showErrorToast } = useToastsContext();
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [selectedApplicant, setSelectedApplicant] =
    useState<ApplicationApplicantDetailsRead | null>(null);
  const uuid = useCurrentApplicationUuid();
  const { updateData } = useStore();
  const {
    data: application,
    isLoading,
    isError,
  } = useGetPersonalLoanApplicationQuery({
    applicationUuid: uuid,
  });
  const { data: applicantDetailsResponse, isError: isApplicantsError } =
    useListApplicationApplicantDetailsQuery({
      applicationUuid: application?.id,
      enabled: Boolean(application?.id),
    });
  const applicants = applicantDetailsResponse?.results ?? [];

  // required for subsequent requests to update the application
  useEffect(() => {
    updateData({ lendingApplicationID: uuid });
  }, [updateData, uuid]);

  useEffect(() => {
    function checkOffers() {
      if (!application) return;
      switch (application?.state) {
        case "creating":
          return;
        case "offers_generated":
          return goToPath(`/olb-personal-loan/${uuid}/offers`, { replace: true });
        case "offer_accepted":
          return goToPath(`/olb-personal-loan/${uuid}/submitted`, { replace: true });
        default:
          return goToPath("/product-selection", { replace: true });
      }
    }
    void checkOffers();
  }, [application, goToPath, uuid]);

  function onClickNext() {
    const incompleteApplicant = applicants.find(isMissingContactDetails);
    if (incompleteApplicant) {
      showErrorToast(
        l10n.getString(
          "confirm-info-missing-details",
          null,
          "Please fill in the missing details to continue."
        )
      );
      onClickEdit(incompleteApplicant);
      return;
    }
    goNext();
  }

  function onClickEdit(applicant: ApplicationApplicantDetailsRead) {
    setSelectedApplicant(applicant);
    setIsDrawerOpen(true);
  }

  if (isError || isApplicantsError) {
    return (
      <PageLayout>
        <ErrorScreen />
      </PageLayout>
    );
  }

  return (
    <>
      <PageLayout
        isLoading={isLoading || !application || !applicantDetailsResponse}
        className={styles.pageLayout}
      >
        <div className={styles.container}>
          <div className={styles.content}>
            <PersonalLoanSelectedProductTag />
            <div className={styles.header}>
              <h1 className="fontColor--heading fontFamily--heading fontSize--heading1 margin--bottom--xs">
                {l10n.getString("confirm-info-heading", null, "First, let’s confirm your details")}
              </h1>
              <div>
                {l10n.getString(
                  "confirm-info-subheading",
                  null,
                  "This helps us verify your identity and create your custom offer."
                )}
              </div>
            </div>
            <div className="margin--bottom--l">
              {applicants.map((applicant) => (
                <ApplicantConfirmCard
                  key={applicant.id}
                  applicant={applicant}
                  askMailingAddress={Boolean(institution.lending_ask_mailing_address)}
                  onClickEdit={onClickEdit}
                />
              ))}
            </div>
            <div className="margin--bottom--xxl">
              <InfoAlert
                message={l10n.getString(
                  "confirm-info-alert",
                  null,
                  "Checking your rate will not affect your credit score."
                )}
              />
            </div>
            <Row alignItems="center" justifyContent="space-between">
              {/* confirm-info is the OLB flow's entry step, so there is no in-flow previous
                  step; Back returns to the Loan Portal it was launched from rather than
                  falling through goBack -> startOver, which lands on web product-selection. */}
              <BackButton onClick={() => goToPath("/portal")} />
              <Button
                label={l10n.getString("confirm-submit-button", null, "Looks good!")}
                onClick={onClickNext}
              />
            </Row>
          </div>
        </div>
      </PageLayout>
      {/* TODO Find a better way to populate lendingApplicant */}
      {application?.id && selectedApplicant && (
        <EditInfoDrawer
          applicationUuid={application.id}
          lendingApplicant={selectedApplicant}
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
        />
      )}
    </>
  );
}

export { ConfirmInfo };
